import { useState } from 'react'
import { useAdminApi } from '../api'
import { t } from '../i18n'
import { useLang } from '../LangContext'
import { formatDate, localeFor } from '../../utils/format'

interface Preview {
  valid: { row: number; nombre: string; fecha_incorporacion: string; grupo: 'con_ficha' | 'sin_ficha' }[]
  errors: { row: number; field: string; message: string }[]
}

interface ConfirmResult {
  created: number
  duplicados: number
}

export function ImportJugadores({ equipoId, onImported }: { equipoId: string; onImported: () => void }) {
  const api = useAdminApi()
  const lang = useLang()
  const locale = localeFor(lang)
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<Preview | null>(null)
  const [resultado, setResultado] = useState<ConfirmResult | null>(null)

  async function handlePlantilla() {
    const { blob, filename } = await api.downloadFile('/admin/equipos/import-jugadores/plantilla')
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  }

  async function handlePreview(e: React.FormEvent) {
    e.preventDefault()
    if (!file) return
    setResultado(null)
    const formData = new FormData()
    formData.append('file', file)
    try {
      setPreview(await api.postForm(`/admin/equipos/${equipoId}/import-jugadores`, formData))
    } catch (error) {
      window.alert(error instanceof Error ? error.message : t('error', lang))
    }
  }

  // Issue #5 del vídeo (30/09/2026): antes esta llamada se asumía exitosa sin mirar la respuesta —
  // un fallo silencioso real (p.ej. si todas las filas se detectan como duplicadas, `created` sale
  // en 0 y el admin nunca se entera). Ahora se muestra siempre el resultado real.
  async function handleConfirm() {
    if (!file) return
    const formData = new FormData()
    formData.append('file', file)
    try {
      const result: ConfirmResult = await api.postForm(`/admin/equipos/${equipoId}/import-jugadores?confirm=true`, formData)
      setResultado(result)
      setPreview(null)
      setFile(null)
      onImported()
    } catch (error) {
      window.alert(error instanceof Error ? error.message : t('error', lang))
    }
  }

  return (
    <div>
      <h3>{t('importarJugadoreak', lang)}</h3>
      <p>{t('formatoImport', lang)}</p>
      <button type="button" onClick={handlePlantilla}>
        {t('descargarPlantilla', lang)}
      </button>
      <form onSubmit={handlePreview}>
        <input
          type="file"
          accept=".xlsx,.xls"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        <button type="submit" disabled={!file}>
          {t('crear', lang)}
        </button>
      </form>

      {preview && (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>{t('fila', lang)}</th>
                <th>{t('nombre', lang)}</th>
                <th>{t('fechaIncorporacion', lang)}</th>
                <th>{t('grupo', lang)}</th>
              </tr>
            </thead>
            <tbody>
              {preview.valid.map((row) => (
                <tr key={row.row}>
                  <td>{row.row}</td>
                  <td>{row.nombre}</td>
                  <td>{formatDate(row.fecha_incorporacion, locale)}</td>
                  <td>{row.grupo}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <table>
            <thead>
              <tr>
                <th>{t('fila', lang)}</th>
                <th>{t('campo', lang)}</th>
                <th>{t('error', lang)}</th>
              </tr>
            </thead>
            <tbody>
              {preview.errors.map((error, i) => (
                <tr key={i}>
                  <td>{error.row}</td>
                  <td>{error.field}</td>
                  <td>{error.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={handleConfirm} disabled={preview.valid.length === 0}>
            {t('confirmar', lang)}
          </button>
        </div>
      )}

      {resultado && (
        <p role="status">
          {t('importResultado', lang)
            .replace('{creados}', String(resultado.created))
            .replace('{duplicados}', String(resultado.duplicados))}
        </p>
      )}
    </div>
  )
}
